<script setup lang="ts">
import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsItemResponse } from '@/types/carts-transactions-api';

import PricingBreakdown from '@/components/booking/PricingBreakdown.vue';

import CartItemDeleteButton from './CartItemDeleteButton.vue';
import CartItemExpirationBadge from './CartItemExpirationBadge.vue';
import CartItemProductInfo from './CartItemProductInfo.vue';
import RequiredContactFields from './RequiredContactFields.vue';

defineProps<{
  item: CartDetailsItemResponse;
  enrichedUnits: Array<{
    id: string;
    description: string;
    quantity: number;
    originalPricePerUnit: number;
    netPricePerUnit: number;
  }>;
  currency?: string;
  currencyPrecision?: number;
  personCount: number;
  dateTime: string;
  isDeleting: boolean;
  isPartialFailure: boolean;
  hasPendingTransaction: boolean;
  cartsTransactionsApi: CartsTransactionsApi;
}>();

const emit = defineEmits<{
  'delete': [];
  'expired': [];
  'update:contactFieldsCompleted': [isCompleted: boolean];
}>();
</script>

<template>
  <div v-if="!isPartialFailure" class="hold">
    <CartItemExpirationBadge
      :utc-expires-at="item.bookingDetails?.utcExpiresAt ?? null"
      @expired="emit('expired')"
    />
    <CartItemDeleteButton
      :is-deleting="isDeleting"
      :disabled="hasPendingTransaction"
      @delete="emit('delete')"
    />
  </div>

  <CartItemProductInfo
    :product-id="item.productId"
    :image-url="item.bookingDetails?.productImageUrl ?? null"
    :product-name="item.bookingDetails?.productName ?? ''"
    :date-time="dateTime"
    :person-count="personCount"
  />

  <RequiredContactFields
    v-if="item.hasRequiredContactFields && !isPartialFailure"
    :item="item"
    :carts-transactions-api="cartsTransactionsApi"
    :disabled="hasPendingTransaction"
    @update:completed="(value: boolean) => emit('update:contactFieldsCompleted', value)"
  />

  <hr aria-hidden="true">

  <PricingBreakdown
    :units="enrichedUnits"
    :currency="currency"
    :currency-precision="currencyPrecision"
    :loading="false"
  />
</template>

<style scoped lang="css">
.hold {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

hr {
  width: 100%;
  border: 1px solid var(--yb-color-lightgray);
}
</style>
